import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  usersApi,
  type CustomersRetentionSummary,
  type LightbaseUser,
} from '@/api/users.api'

const EMPTY_RETENTION: CustomersRetentionSummary = {
  trials_ending_7d: 0,
  renewals_7d: 0,
  access_ending_7d: 0,
  at_risk: 0,
  cancelling: 0,
  at_risk_inactive_days: 5,
  upcoming_event_days: 7,
}

export const useCustomersStore = defineStore('customers', () => {
  const users = ref<LightbaseUser[]>([])
  const retention = ref<CustomersRetentionSummary>({ ...EMPTY_RETENTION })
  const loading = ref(false)
  const error = ref('')
  const loaded = ref(false)
  const spotlightOpen = ref(false)
  const selectedUser = ref<LightbaseUser | null>(null)

  let inFlight: Promise<void> | null = null

  async function load(force = false) {
    if (inFlight) return inFlight
    if (loaded.value && !force) return

    loading.value = true
    error.value = ''
    inFlight = (async () => {
      try {
        const { data, status } = await usersApi.list()
        if (status !== 200) {
          error.value = 'Failed to load customers'
          return
        }
        users.value = data?.users ?? []
        if (data?.retention) {
          retention.value = data.retention
        }
        loaded.value = true
      } catch (e: unknown) {
        error.value = e instanceof Error ? e.message : 'Failed to load customers'
      } finally {
        loading.value = false
        inFlight = null
      }
    })()
    return inFlight
  }

  function openSpotlight() {
    spotlightOpen.value = true
    void load()
  }

  function closeSpotlight() {
    spotlightOpen.value = false
  }

  function selectUser(user: LightbaseUser) {
    selectedUser.value = user
  }

  function closeDetail() {
    selectedUser.value = null
  }

  return {
    users,
    retention,
    loading,
    error,
    loaded,
    spotlightOpen,
    selectedUser,
    load,
    openSpotlight,
    closeSpotlight,
    selectUser,
    closeDetail,
  }
})
